import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button, Money } from '@clari/ui'
import { ErrorBanner } from '@/components/ErrorBanner'
import { apiErrorMessage } from '@/lib/api-error'
import { adminApi } from '@/lib/admin-api'
import { formatDate } from '@/lib/format'
import type { AdminRevenueFigureDto } from '@/lib/types'

const EMPTY = {
  period: '',
  subscriptions: '',
  interchange: '',
  fx_margin: '',
  paylink: '',
  notes: '',
}
type FormState = typeof EMPTY

function eurosToCents(v: string): number {
  const n = Number.parseFloat(v)
  return Number.isFinite(n) ? Math.round(n * 100) : 0
}
function centsToEuros(c: number): string {
  return (c / 100).toString()
}

/**
 * Business-model revenue figures (USP #7). Operators enter monthly revenue in
 * euros; only an explicit Publish surfaces a month on the public page, and every
 * mutation is audited server-side.
 */
export function BusinessModel() {
  const { t, i18n } = useTranslation('admin')
  const qc = useQueryClient()
  const [form, setForm] = useState<FormState>({ ...EMPTY })
  const [actionError, setActionError] = useState<string | null>(null)

  const query = useQuery({ queryKey: ['admin-revenue'], queryFn: () => adminApi.listRevenue() })

  const upsert = useMutation({
    mutationFn: () =>
      adminApi.upsertRevenue({
        period: form.period,
        currency: 'EUR',
        subscriptions_cents: eurosToCents(form.subscriptions),
        interchange_cents: eurosToCents(form.interchange),
        fx_margin_cents: eurosToCents(form.fx_margin),
        paylink_cents: eurosToCents(form.paylink),
        ...(form.notes.trim() ? { notes: form.notes.trim() } : {}),
      }),
    onSuccess: async () => {
      setForm({ ...EMPTY })
      setActionError(null)
      await qc.invalidateQueries({ queryKey: ['admin-revenue'] })
    },
    onError: (e) => setActionError(apiErrorMessage(e)),
  })

  const publish = useMutation({
    mutationFn: (vars: { id: string; published: boolean }) =>
      adminApi.publishRevenue(vars.id, vars.published),
    onSuccess: async () => {
      setActionError(null)
      await qc.invalidateQueries({ queryKey: ['admin-revenue'] })
    },
    onError: (e) => setActionError(apiErrorMessage(e)),
  })

  function field(key: keyof FormState) {
    return (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm((f) => ({ ...f, [key]: e.target.value }))
  }

  function loadIntoForm(r: AdminRevenueFigureDto) {
    setForm({
      period: r.period,
      subscriptions: centsToEuros(r.subscriptions_cents),
      interchange: centsToEuros(r.interchange_cents),
      fx_margin: centsToEuros(r.fx_margin_cents),
      paylink: centsToEuros(r.paylink_cents),
      notes: r.notes ?? '',
    })
  }

  const periodValid = /^\d{4}-\d{2}$/.test(form.period)
  const busy = upsert.isPending || publish.isPending

  const amountFields: { key: keyof FormState; label: string }[] = [
    { key: 'subscriptions', label: t('business_model.subscriptions') },
    { key: 'interchange', label: t('business_model.interchange') },
    { key: 'fx_margin', label: t('business_model.fx_margin') },
    { key: 'paylink', label: t('business_model.paylink') },
  ]

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-2xl font-semibold">{t('business_model.title')}</h1>
        <p className="text-text-muted">{t('business_model.subtitle')}</p>
      </div>

      {actionError ? <ErrorBanner message={actionError} /> : null}

      <form
        className="flex flex-col gap-4 rounded-lg border border-border p-4"
        onSubmit={(e) => {
          e.preventDefault()
          if (periodValid) upsert.mutate()
        }}
      >
        <h2 className="font-medium">{t('business_model.form_title')}</h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-text-muted">{t('business_model.period')}</span>
            <input
              className="clari-input"
              placeholder="2026-05"
              value={form.period}
              onChange={field('period')}
            />
          </label>
          {amountFields.map(({ key, label }) => (
            <label key={key} className="flex flex-col gap-1 text-sm">
              <span className="text-text-muted">{label}</span>
              <input
                className="clari-input"
                type="number"
                min="0"
                step="0.01"
                inputMode="decimal"
                value={form[key]}
                onChange={field(key)}
              />
            </label>
          ))}
        </div>
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-text-muted">{t('business_model.notes')}</span>
          <textarea className="clari-input" rows={2} value={form.notes} onChange={field('notes')} />
        </label>
        <div className="flex gap-2">
          <Button type="submit" disabled={!periodValid || busy}>
            {t('business_model.save')}
          </Button>
          {form.period ? (
            <Button type="button" variant="secondary" disabled={busy} onClick={() => setForm({ ...EMPTY })}>
              {t('common.back')}
            </Button>
          ) : null}
        </div>
      </form>

      <div className="flex flex-col gap-2">
        <h2 className="font-medium">{t('business_model.list_title')}</h2>
        {query.isError ? <ErrorBanner message={apiErrorMessage(query.error)} /> : null}
        {query.isLoading ? <p className="text-text-muted">{t('common.loading')}</p> : null}

        {query.data ? (
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-border text-text-muted">
                <tr>
                  <th className="px-4 py-2 font-medium">{t('business_model.col_period')}</th>
                  <th className="px-4 py-2 font-medium">{t('business_model.col_total')}</th>
                  <th className="px-4 py-2 font-medium">{t('business_model.col_status')}</th>
                  <th className="px-4 py-2 font-medium">{t('business_model.col_actions')}</th>
                </tr>
              </thead>
              <tbody>
                {query.data.items.map((r) => (
                  <tr key={r.id} className="border-b border-border last:border-0">
                    <td className="px-4 py-2 font-mono">{r.period}</td>
                    <td className="px-4 py-2">
                      <Money amountCents={r.total_cents} currency={r.currency} locale={i18n.language} />
                    </td>
                    <td className="px-4 py-2">
                      {r.published ? (
                        <span className="text-success">{t('business_model.published')}</span>
                      ) : (
                        <span className="text-text-muted">{t('business_model.draft')}</span>
                      )}
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex gap-2">
                        <Button size="sm" variant="secondary" disabled={busy} onClick={() => loadIntoForm(r)}>
                          {t('business_model.edit')}
                        </Button>
                        <Button
                          size="sm"
                          disabled={busy}
                          onClick={() => publish.mutate({ id: r.id, published: !r.published })}
                        >
                          {r.published ? t('business_model.unpublish') : t('business_model.publish')}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {query.data.items.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-6 text-center text-text-muted">
                      {t('business_model.empty')}
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        ) : null}
        {query.data && query.data.items.length > 0 ? (
          <p className="text-xs text-text-subtle">
            {t('business_model.updated_hint', {
              date: formatDate(query.data.items[0]?.updated_at ?? null, i18n.language),
            })}
          </p>
        ) : null}
      </div>
    </div>
  )
}
